import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHero } from "@/components/common/PageHero";
import { Reveal } from "@/components/common/Reveal";
import { CtaBand } from "@/components/common/CtaBand";
import { BeforeAfterGallery } from "@/components/common/BeforeAfterGallery";
import { Seo } from "@/components/common/Seo";
import { buildBreadcrumbSchema } from "@/lib/schema";
import { PROBLEMS } from "@/data/site";
import { ToothIcon } from "@/components/common/ToothIcon";

export default function Problems() {
  return (
    <div data-testid="problems-page">
      <Seo
        title="Common Dental Problems | Dr. Ankesh Seth, Dentist in Varanasi"
        description="Identify common dental problems like tooth pain, bleeding gums, sensitive teeth and missing teeth — and find the right treatment with Dr. Ankesh Seth in Varanasi."
        path="/problems"
        jsonLd={buildBreadcrumbSchema([{ name: "Home", path: "/" }, { name: "Dental Problems", path: "/problems" }])}
      />
      <PageHero
        eyebrow="Dental Problems"
        titleLines={["Common Dental", "Problems"]}
        subtitle="Recognise the symptoms early. Explore common dental problems and discover the right treatment before small issues become big ones."
        crumbs={[{ label: "Dental Problems" }]}
      />

      <section className="bg-white">
        <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-12 md:py-32 lg:px-20">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {PROBLEMS.map((p, i) => (
              <Reveal key={p.slug} delay={(i % 4) * 0.06}>
                <Link
                  to={`/problems/${p.slug}`}
                  data-testid={`problem-item-${p.slug}`}
                  className="group flex h-full flex-col overflow-hidden rounded-2xl border border-brand-line bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_30px_60px_-35px_rgba(2,125,199,0.5)]"
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={p.image}
                      alt={`${p.name} — symptoms & treatment in Varanasi`}
                      loading="lazy"
                      width="480"
                      height="360"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <span className="absolute left-3 top-3 flex h-10 w-10 items-center justify-center rounded-xl bg-white/90 text-brand-blue backdrop-blur-md">
                      <ToothIcon className="h-5 w-5" />
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="font-display text-xl font-medium text-brand-dark">{p.name}</h3>
                    <p className="mt-2 flex-1 text-sm text-brand-muted">{p.short}</p>
                    <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-blue">
                      Find treatment
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <BeforeAfterGallery muted />
      <CtaBand />
    </div>
  );
}
